import { useTranslationRef } from '@backstage/frontend-plugin-api';
import type {
  EnvironmentConfigSummary,
  QuantityPair,
} from '@internal/plugin-waadoo-config-common';
import {
  KeptLines,
  projectEnvironmentsTranslationRef,
  type KeptSummaryProps,
} from '@internal/plugin-waadoo-core';
import { odooTranslationRef } from './translation';

/**
 * The facts Odoo's « Ce qui ne change pas » says — the shape of the former
 * Odoo summary of the promotion route (before step 6 of the genericity
 * plan), each one absent when not read.
 */
export interface OdooKeptFacts {
  workers?: number;
  odooCpu?: QuantityPair;
  odooMemory?: QuantityPair;
  filestoreSize?: string;
  /** No filestore volume: the attachments on S3 (its size hidden). */
  attachmentsS3?: true;
  databaseSize?: string;
  autoscaling?: { enabled: boolean; min?: number; max?: number };
  cache?: boolean;
  databaseInstances?: number;
}

/**
 * Those facts read off the generic summary (`summarizeEnvironmentConfig`):
 * the main component's resources, files volume and autoscaling, the
 * `workers` setting (a whole number of 0 or more, as before), the
 * database's size (Odoo has one, its CloudNativePG component) and
 * instances, the Valkey component's `enabled`; `attachmentsS3` when the
 * summary left the filestore's size out (`hiddenFields`, its `when`: the
 * attachments on S3). Undefined when none is read, as the former summary
 * was.
 */
export function odooKeptFacts(
  summary: EnvironmentConfigSummary | undefined,
): OdooKeptFacts | undefined {
  if (!summary) return undefined;
  const main = summary.capacity.find(item => item.main);
  const cnpg = summary.capacity.find(item => item.kind === 'cnpg');
  const cache = summary.capacity.find(item => item.kind === 'valkey');
  const workers = summary.settings.find(item => item.field === 'workers');
  const facts: OdooKeptFacts = {
    workers:
      typeof workers?.value === 'number' &&
      Number.isInteger(workers.value) &&
      workers.value >= 0
        ? workers.value
        : undefined,
    odooCpu: main?.cpu,
    odooMemory: main?.memory,
    filestoreSize: summary.volumes.find(
      item => item.role === 'volume' && item.main,
    )?.size,
    attachmentsS3: summary.hiddenFields?.includes('filestoreSize')
      ? true
      : undefined,
    databaseSize: summary.volumes.find(item => item.role === 'databaseSize')
      ?.size,
    autoscaling: main?.autoscaling,
    cache: cache?.enabled,
    databaseInstances: cnpg?.pods,
  };
  const read = Object.fromEntries(
    Object.entries(facts).filter(([, value]) => value !== undefined),
  ) as OdooKeptFacts;
  return Object.keys(read).length > 0 ? read : undefined;
}

/** `100m / 1600m`, either side alone when the other is unknown. */
function range(value?: QuantityPair): string {
  return [value?.request, value?.limit].filter(Boolean).join(' / ');
}

/**
 * « Ce qui ne change pas » of the promotion dialog for an Odoo project
 * (`AppTypeModule.promotion.KeptSummary`, genericity plan, step 6): the
 * core's list until then, kept word for word — Odoo's capacity, its
 * workers, « fichiers » (or the attachments on S3) and « base », its HPA or
 * its single pod, the Valkey sessions, the PostgreSQL instances, its data
 * (the attachments said on S3 when they are) —, from `odooKeptFacts`.
 */
export function OdooKeptSummary(props: KeptSummaryProps) {
  const { target } = props;
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  const { t: tOdoo } = useTranslationRef(odooTranslationRef);
  const config = odooKeptFacts(props.config);
  const lines: string[] = [];
  if (config?.odooCpu || config?.odooMemory) {
    lines.push(
      tOdoo('promote.keptCapacity', {
        cpu: range(config.odooCpu) || '?',
        memory: range(config.odooMemory) || '?',
      }),
    );
  }
  if (config?.workers !== undefined) {
    lines.push(
      tOdoo('promote.keptWorkers', { workers: String(config.workers) }),
    );
  }
  if (config?.attachmentsS3) {
    lines.push(
      tOdoo('promote.keptVolumesS3', {
        database: config.databaseSize ?? '?',
      }),
    );
  } else if (config?.filestoreSize || config?.databaseSize) {
    lines.push(
      tOdoo('promote.keptVolumes', {
        filestore: config.filestoreSize ?? '?',
        database: config.databaseSize ?? '?',
      }),
    );
  }
  if (config?.autoscaling) {
    lines.push(
      config.autoscaling.enabled
        ? t('promote.keptHpa', {
            min: String(config.autoscaling.min ?? '?'),
            max: String(config.autoscaling.max ?? '?'),
          })
        : tOdoo('promote.keptSinglePod'),
    );
  }
  if (config?.cache) {
    lines.push(tOdoo('promote.keptCache'));
  }
  if (config?.databaseInstances !== undefined) {
    lines.push(
      t('promote.keptDatabaseInstances', {
        instances: String(config.databaseInstances),
      }),
    );
  }
  if (lines.length === 0) {
    lines.push(t('promote.keptGeneric', { target }));
  }
  lines.push(
    tOdoo(config?.attachmentsS3 ? 'promote.keptDataS3' : 'promote.keptData', {
      target,
    }),
  );
  return <KeptLines lines={lines} />;
}
